import { useMergeRefs } from "@floating-ui/react";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { forwardRef, HTMLProps, ReactNode, useEffect, useMemo, useRef } from "react";

import { useConfiguration } from "~/contexts/configuration";
import powerReviewUtils, { PropsPowerReviewsCultureConfig } from "~/utils/power-reviews-utils";

import Container from "../common/container";
import PowerReviewsUiScript from "../common/powerreviews-ui-script";
import styles from "./product-reviews.module.scss";

type PropsProductReviews = {
  parent_backend_id: string | number;
  children?: ReactNode;
};

declare global {
  var pwr: any;
}

const ProductReviews = forwardRef<HTMLElement, HTMLProps<HTMLElement> & PropsProductReviews>(function ProductReviews(
  { parent_backend_id, children },
  propRef
) {
  const router = useRouter();
  const { configuration } = useConfiguration();
  const t = useTranslations();
  const childrenRef = (children as any)?.ref;
  const refPrReviewdisplay = useRef(null);
  const refProductReviews = useMergeRefs([propRef, childrenRef]);

  useEffect(() => {
    const mutationObserver = new MutationObserver(
      (mutationList: MutationRecord[], observer: MutationObserver): void => {
        for (const mutation of mutationList) {
          if (mutation.type === "childList") {
            const mutationTargetEl = mutation.target as HTMLElement;

            const noReviewsElement: HTMLDivElement | null = mutationTargetEl.querySelector(
              ".pr-review-display .pr-rd-no-reviews"
            );

            if (noReviewsElement) {
              noReviewsElement.innerHTML = `<p>${t("error.noReviews")}</p>`;
              observer.disconnect();
            }
          }
        }
      }
    );

    if (refPrReviewdisplay.current) {
      mutationObserver.observe(refPrReviewdisplay.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      mutationObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refPrReviewdisplay.current]);

  const pwrConfig: PropsPowerReviewsCultureConfig = useMemo(
    () => powerReviewUtils.getPwrConfiguration(configuration, router),
    [configuration, router]
  );

  return pwrConfig ? (
    <Container ref={refProductReviews} className={styles.container}>
      <p className={styles.title}>{t("generic.reviews")}</p>
      {children}
      {/* List of reviews */}
      <div id="pr-reviewdisplay" ref={refPrReviewdisplay} className={styles.powerReviewsContainer} />
      {/* PowerReviews UI (Reviews) Script */}
      <PowerReviewsUiScript parent_backend_id={parent_backend_id} />
    </Container>
  ) : null;
});

export default ProductReviews;
